import { ProBadge } from "@/components/ProBadge";
import { Brain, Warning, CheckCircle, Eye, TrendUp, TrendDown, Pulse, Minus } from "@phosphor-icons/react";
import InfoTip from "@/components/InfoTip";

const LEVEL_STYLE = {
  elevated: { color: "#EF6A56", icon: Warning },
  watch: { color: "#E8A54B", icon: Eye },
  good: { color: "#16BC9C", icon: CheckCircle },
};

function BaselineRow({ m }) {
  const hasToday = m.today !== null && m.today !== undefined;
  const pct = m.pct;
  const neutral = !hasToday || pct === null || Math.abs(pct) < 3;
  const isGood = !neutral && ((pct > 0 && m.good_dir === "up") || (pct < 0 && m.good_dir === "down"));
  const color = neutral ? "#7C8AA5" : isGood ? "#16BC9C" : "#EF6A56";
  const Arrow = neutral ? Minus : pct > 0 ? TrendUp : TrendDown;
  const fmt = (v) => (m.key === "steps" ? Math.round(v).toLocaleString() : v);
  return (
    <div className="flex items-center gap-2 text-sm py-1.5" data-testid={`baseline-${m.key}`}>
      <span className="text-muted2 w-24 shrink-0">{m.label}</span>
      <span className="text-xs text-muted2">norm {fmt(m.baseline)}{m.unit && ` ${m.unit}`}</span>
      <span className="ml-auto font-medium">{hasToday ? `${fmt(m.today)}${m.unit ? ` ${m.unit}` : ""}` : "—"}</span>
      {hasToday && pct !== null && (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold w-14 justify-end" style={{ color }}>
          <Arrow size={13} weight="bold" />
          {pct > 0 ? "+" : ""}{pct}%
        </span>
      )}
    </div>
  );
}

const DIRECTION_META = {
  improving: { label: "Improving", color: "#16BC9C", icon: TrendUp },
  steady: { label: "Steady", color: "#7C8AA5", icon: Minus },
  strained: { label: "Strained", color: "#E8A54B", icon: TrendDown },
  spiraling: { label: "Needs care", color: "#EF6A56", icon: TrendDown },
};

export default function TwinInsights({ twin, insight }) {
  if (!twin) return null;
  const baselines = twin.baselines?.metrics || [];
  const traj = twin.trajectory;
  const dir = traj && DIRECTION_META[traj.direction];
  return (
    <div className="clay p-6" data-testid="twin-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-sage/15 flex items-center justify-center">
          <Brain size={18} weight="fill" color="#16BC9C" />
        </div>
        <span className="label-eyebrow flex items-center gap-1.5">Digital Twin — predictions <InfoTip termId="digital-twin" /> <ProBadge testId="pro-badge-twin" /></span>
      </div>

      {insight && (
        <div className="mt-4 rounded-2xl bg-brand/8 border border-brand/15 px-4 py-3">
          <p className="text-sm leading-relaxed text-ink">{insight.message}</p>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 text-sm">
        <TrendUp size={16} weight="fill" color="#16BC9C" />
        <span className="text-muted2">Projected 30-day score</span>
        <span className="ml-auto font-semibold text-sage">{twin.projected_score_30d}</span>
      </div>

      {dir && (
        <div className="flex items-center gap-2 mt-2 text-sm" data-testid="trajectory-indicator">
          <dir.icon size={16} weight="bold" color={dir.color} />
          <span className="text-muted2">14-day direction</span>
          <span className="ml-auto font-semibold" style={{ color: dir.color }}>
            {dir.label}
            <span className="font-normal text-xs text-muted2 ml-1.5">· {traj.confidence}</span>
          </span>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {(twin.risks || []).map((r, i) => {
          const st = LEVEL_STYLE[r.level] || LEVEL_STYLE.watch;
          const Icon = st.icon;
          return (
            <div key={i} className="flex items-start gap-2.5 rounded-2xl bg-secondary/60 px-4 py-2.5">
              <Icon size={17} weight="fill" color={st.color} className="mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium">{r.label}</div>
                <div className="text-xs text-muted2">{r.note}</div>
              </div>
            </div>
          );
        })}
      </div>

      {baselines.length > 0 && (
        <div className="mt-5 rounded-2xl bg-secondary/60 px-4 py-3" data-testid="baseline-panel">
          <div className="flex items-center gap-1.5">
            <Pulse size={15} weight="fill" color="#16BC9C" />
            <span className="text-xs font-medium text-muted2 uppercase tracking-wide">
              Today vs your {twin.baselines.window_days}-day baseline
            </span>
          </div>
          <div className="mt-2 divide-y divide-border/60">
            {baselines.map((m) => <BaselineRow key={m.key} m={m} />)}
          </div>
        </div>
      )}
    </div>
  );
}
